import React, {useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {SettingsStackParamList} from '@/app/routes';
import {
  exportDepartmentAttendance,
  fetchControlDepartments,
  fetchDepartmentAttendance,
} from '@/entities/attendance/api';
import {currentMonth, periodKey, type Period} from '@/entities/attendance/period';
import {fetchDepartments} from '@/entities/department/api';
import {useFleetFeatures} from '@/entities/device/fleet';
import {useAuthStore} from '@/entities/session/auth-store';
import {departmentTypeLabelKey} from '@/features/manage-departments/lib/departmentType';
import {DepartmentScheduleSheet} from '@/features/manage-department-schedule/ui/DepartmentScheduleSheet';
import {DepartmentForm} from '@/features/manage-departments/ui/DepartmentForm';
import {DepartmentCameras} from '@/features/view-live-video/ui/DepartmentCameras';
import {dailyTotals} from '@/features/view-attendance/lib/aggregate';
import {reportFileName} from '@/features/view-attendance/lib/csv';
import {
  describePeriod,
  usePeriodLabel,
} from '@/features/view-attendance/lib/period-label';
import {daysCsv, employeesCsv} from '@/features/view-attendance/lib/exports';
import {DailyChart} from '@/features/view-attendance/ui/DailyChart';
import {EmployeeReportList} from '@/features/view-attendance/ui/EmployeeReportList';
import {ExportBar} from '@/features/view-attendance/ui/ExportBar';
import {PeriodPicker} from '@/features/view-attendance/ui/PeriodPicker';
import {ScheduleSummary} from '@/features/view-attendance/ui/ScheduleSummary';
import {SummaryBoard} from '@/features/view-attendance/ui/SummaryBoard';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Page} from '@/shared/ui/page';
import {SettingsNav, useInSettings} from '@/pages/settings/SettingsNav';
import {Section} from '@/shared/ui/section';
import {CalendarList} from '@/features/manage-calendar/ui/CalendarList';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * One department, as a place rather than as a row in two unrelated lists.
 *
 * Before this screen a department was split across the app: «Ещё → Отделы» opened a
 * three-field edit form (name, type, code) and «Отчёты → По отделам» opened its
 * attendance, and neither knew about the other — so "open the department and manage
 * it" was a thing the app could not do. This is that place: what it is, who is in it,
 * the schedule its verdicts are computed against, its attendance for a period, and the
 * spreadsheet.
 *
 * ── The two id spaces ──────────────────────────────────────────────────────────
 * A department is authored in sv_organization and PROJECTED into sv_control, and the
 * two keep different ids: the projection carries `organization_department_id` as a
 * soft, unenforced correlation back to its author row. This screen is opened from the
 * organization list, so it arrives holding an ORG id — and every attendance route is
 * keyed by the CONTROL id. `controlDept` below is that correlation, resolved once and
 * used for everything downstream. `DepartmentPicker` does the same join for the same
 * reason; if that mapping is ever centralised, both should move together.
 *
 * A department with no projection yet is a real state, not an error: it was just
 * created, or the correlation is missing. Attendance and export are unavailable then,
 * and the screen says so instead of drawing a board full of confident zeros.
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'Department'>;

const styles = StyleSheet.create({
  /** Нутро карточки-шапки: строка с типом и кодом, под ней счётчики. */
  identity: {gap: spacing(3)},
  headRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing(3),
  },
  headText: {flex: 1, gap: 2},
  counts: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(6)},
  scheduleActions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(3),
  },
  scheduleNote: {flex: 1, minWidth: 200},
  count: {gap: 2},
});

export function DepartmentScreen({navigation, route}: Props) {
  const {t, i18n} = useTranslation();
  const p = useTheme();
  const {label: periodTitle} = usePeriodLabel();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const {deptId, name} = route.params;
  const inSettings = useInSettings();
  // Без терминалов у отдела нет ни графика, ни явки: остаётся то, что он такое.
  const {attendance} = useFleetFeatures().features;

  const now = useMemo(() => new Date(), []);
  const [period, setPeriod] = useState<Period>(() => currentMonth(now));
  const [editing, setEditing] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(false);

  // Same cache key the departments list uses, so arriving here is instant.
  const orgQuery = useQuery({
    queryKey: ['departments', companyId],
    queryFn: () => fetchDepartments(companyId as string, {perPage: 100}),
    enabled: !!companyId,
  });

  const controlQuery = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId as string),
    enabled: !!companyId,
  });

  const department = orgQuery.data?.data.find(d => d.id === deptId);

  // The correlation, and the defensive second arm: if this screen is ever reached
  // holding a CONTROL id, match that too rather than silently showing no data.
  const controlDept = useMemo(() => {
    const rows = controlQuery.data?.departments ?? [];
    return (
      rows.find(d => d.organization_department_id === deptId) ??
      rows.find(d => d.department_id === deptId)
    );
  }, [controlQuery.data, deptId]);

  const controlId = controlDept?.department_id;

  const report = useQuery({
    queryKey: ['attendance-department', companyId, controlId, periodKey(period)],
    queryFn: () =>
      fetchDepartmentAttendance(companyId as string, controlId as string, period),
    enabled: !!companyId && !!controlId && attendance,
  });

  const days = report.data?.days ?? [];
  // Keyed on `report.data`, not on `days`: the `?? []` fallback is a new array every
  // render and would defeat the memo. Declared above the early return — a hook that
  // runs only when a company is active changes the hook ORDER between renders.
  const totals = useMemo(() => dailyTotals(report.data?.days ?? []), [report.data]);

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.departments" />;
  }

  const title = department?.name ?? controlDept?.name ?? name;
  // Через общий помощник, а не своей строкой: собранный здесь «2026-08-01 — 2026-08-26»
  // был третьим написанием одного и того же периода — и попадал в имя файла выгрузки,
  // где расходился с тем, как тот же период назван на экране.
  const periodLabel = periodTitle(report.data ?? describePeriod(period));
  const fileStem = reportFileName(
    [t('reports.fileAttendance'), title, periodLabel],
    '',
  ).slice(0, -1);

  /**
   * Действующий график отдела: тот, по которому отчёт СЧИТАЛ, если отчёт уже ответил,
   * и тот, что несёт проекция, пока он в пути. Порядок именно такой — форма правки
   * должна открыться на том графике, который объясняет цифры на экране.
   */
  const schedule =
    report.data?.schedule ?? controlDept?.attendance_schedule ?? null;

  const refreshing =
    orgQuery.isRefetching || controlQuery.isRefetching || report.isRefetching;

  const refetchAll = () => {
    void orgQuery.refetch();
    void controlQuery.refetch();
    if (controlId) {
      void report.refetch();
    }
  };

  /** The identity block: what this department is, and the way into its three fields. */
  const identity = (
    <Card padding={4} style={styles.identity} testID="department-identity">
      <View style={styles.headRow}>
        <View style={styles.headText}>
          {/* What kind of department this is, and its code. The NAME is not repeated
              here — the page header three lines up already carries it, and a title
              stated twice on one screen is the lower, smaller copy wasted. */}
          <AppText variant="eyebrow">
            {department
              ? t(departmentTypeLabelKey(department.department_type))
              : t('department.unprojectedType')}
          </AppText>
          {department?.code ? (
            <AppText variant="mono">{department.code}</AppText>
          ) : null}
        </View>

        {/*
          Вход в правку — и единственный вход в УДАЛЕНИЕ.

          Его здесь не было. Состояние `editing` объявлялось, лист правки был к
          нему привязан, а `setEditing(true)` не вызывался ниоткуда: и правка, и
          удаление отдела были написаны целиком и недостижимы ни одним нажатием.
          Соседний комментарий про график («правятся через „Изменить“ в шапке»)
          ссылался на эту кнопку как на существующую.

          Только когда отдел найден в списке sv_organization: лист правки берёт
          оттуда имя, тип и код, и открывать его на карточке, которую знает лишь
          проекция sv_control, значило бы предложить править пустоту.
        */}
        {department ? (
          <AppButton
            title={t('actions.edit')}
            tone="soft"
            size="sm"
            icon="edit"
            onPress={() => setEditing(true)}
            testID="department-edit"
          />
        ) : null}
      </View>

      {controlDept ? (
        <View style={styles.counts}>
          {attendance ? (
            <View style={styles.count}>
              <AppText variant="monoLg">{controlDept.personnel_count}</AppText>
              <AppText variant="micro">{t('department.personnelCount')}</AppText>
            </View>
          ) : null}
          <View style={styles.count}>
            <AppText variant="monoLg">{controlDept.devices_count}</AppText>
            <AppText variant="micro">{t('department.devicesCount')}</AppText>
          </View>
        </View>
      ) : null}
    </Card>
  );

  /**
   * Камеры отдела — и в кабинете из одних камер, и в полном. Переход в просмотр
   * несёт оба id отдела: отдел устройству назначается id проекции, но строка, на
   * которой он записан, могла прийти и с id sv_organization.
   */
  const departmentIds = [deptId, controlId].filter((v): v is string => !!v);
  const cameras = (
    <Section>
      <DepartmentCameras
        companyId={companyId}
        departmentIds={departmentIds}
        onWatch={() =>
          // Вкладка «Камеры» — соседняя; у сотрудника её нет, и переход молчит.
          navigation.getParent()?.navigate('Cameras', {
            screen: 'CameraWall',
            params: {dept: departmentIds.join(',')},
          })
        }
      />
    </Section>
  );

  let body: React.ReactNode;

  if (orgQuery.isPending || controlQuery.isPending) {
    body = (
      <Section>
        <Skeleton height={120} radius={radii.lg} />
        <Skeleton height={190} radius={radii.lg} />
      </Section>
    );
  } else if (orgQuery.isError || controlQuery.isError) {
    body = (
      <EmptyState
        tone="error"
        title={t('department.error')}
        actionLabel={t('actions.retry')}
        onAction={refetchAll}
      />
    );
  } else if (!attendance) {
    body = (
      <>
        <Section>{identity}</Section>
        {cameras}
      </>
    );
  } else if (!controlDept) {
    // Authored but not projected: identity is editable, attendance does not exist yet.
    body = (
      <>
        <Section>{identity}</Section>
        <Section>
          <Banner tone="info" title={t('department.notProjected')} />
        </Section>
      </>
    );
  } else {
    body = (
      <>
        <Section>{identity}</Section>
        {cameras}

        {/*
          Правится прямо здесь — с 26 августа 2026 у графика отдела есть своя ручка
          (`PATCH /control/departments/{id}/schedule`). До неё этот блок был read-only
          с подписью «задаётся на стороне сервера», что на деле означало «никем»:
          написать график было нечем ни отсюда, ни откуда-либо ещё.

          Кнопка ведёт не в общую форму отдела: маршрут пишет ТОЛЬКО график, а имя, код
          и связь с оргструктурой живут в sv_organization и правятся через «Изменить» в
          шапке. Два разных сохранения — две разные кнопки.
        */}
        <Section>
          <ScheduleSummary schedule={schedule} source="department" />
          <View style={styles.scheduleActions}>
            <AppButton
              title={t('department.scheduleEdit')}
              tone="soft"
              size="sm"
              icon="clock"
              onPress={() => setEditingSchedule(true)}
              testID="department-schedule-edit"
            />
            <AppText variant="micro" color={p.mutedForeground} style={styles.scheduleNote}>
              {t('department.schedulePersonalNote')}
            </AppText>
          </View>
        </Section>

        {/* Праздники и переносы отдела (SV-54): свои правятся, компании и
            вышестоящих отделов видны с пометкой. Ключ — id проекции sv_control. */}
        {controlId ? (
          <Section title={t('calendar.departmentTitle')}>
            <CalendarList companyId={companyId} departmentId={controlId} />
          </Section>
        ) : null}

        <Section>
          <PeriodPicker period={period} onChange={setPeriod} now={now} />
        </Section>

        {report.isPending ? (
          <Section>
            <Skeleton height={190} radius={radii.lg} />
            <Skeleton height={140} radius={radii.lg} />
          </Section>
        ) : report.isError ? (
          <EmptyState
            tone="error"
            title={t('reports.error')}
            actionLabel={t('actions.retry')}
            onAction={() => void report.refetch()}
          />
        ) : (
          <>
            <Section>
              <SummaryBoard summary={report.data.summary} />
            </Section>
            <Section>
              <DailyChart days={totals} />
            </Section>
            <Section title={t('department.roster')}>
              <EmployeeReportList
                employees={report.data.employees}
                onOpen={row =>
                  // Cross-tab: the employee report lives in the Reports stack. Guarded
                  // rather than asserted — the Reports tab is absent for the employee
                  // persona, and a missing parent must not throw on a tap.
                  navigation
                    .getParent()
                    ?.navigate('Reports', {
                      screen: 'EmployeeReport',
                      params: {
                        recordId: row.record_id,
                        name: row.full_name,
                        period: periodKey(period),
                      },
                    })
                }
              />
            </Section>
            <Section>
              <ExportBar
                fileName={fileStem}
                onServer={format =>
                  exportDepartmentAttendance(companyId, controlDept.department_id, period, format, i18n.language)
                }
                onLocal={() =>
                  days.length > 0
                    ? daysCsv(days, t)
                    : employeesCsv(report.data.employees, t)
                }
              />
            </Section>
          </>
        )}
      </>
    );
  }

  return (
    <Page
      sideNav={inSettings ? <SettingsNav /> : undefined}
      scroll
      testID="department-screen"
      title={title}
      subtitle={t('department.subtitle')}
      refreshing={refreshing}
      onRefresh={refetchAll}>
      {body}

      {/* key flips on open so the form re-seeds from props rather than reusing state. */}
      <DepartmentForm
        key={editing ? `edit-${deptId}` : 'edit-none'}
        companyId={companyId}
        department={department}
        visible={editing}
        onClose={() => setEditing(false)}
        // Отдела больше нет: оставаться на его карточке — это заголовок с именем
        // удалённого и три запроса, которым некуда идти.
        onDeleted={() => navigation.popTo('Departments')}
        filedUnder={
          controlDept
            ? {
                personnel: controlDept.personnel_count,
                devices: controlDept.devices_count,
              }
            : undefined
        }
      />

      {/* Только при наличии проекции: график живёт в sv_control и ключуется её id.
          Тот же приём с `key` — форма пересеивается из графика, а не помнит брошенный. */}
      {controlDept && attendance ? (
        <DepartmentScheduleSheet
          key={editingSchedule ? `schedule-${controlDept.department_id}` : 'schedule-none'}
          companyId={companyId}
          departmentId={controlDept.department_id}
          schedule={schedule}
          visible={editingSchedule}
          onClose={() => setEditingSchedule(false)}
        />
      ) : null}
    </Page>
  );
}
